import { useCallback, useEffect, useMemo, useState } from 'react'
import { FgHelp } from '../help/FgHelp'
import { MemberPicker } from './MemberPicker'
import {
  activeMeasures,
  describeChange,
  emptySpec,
  excelDestinations,
  fieldLabel,
  latestClosedMonth,
  periodDimension,
  specProblems,
  summarizeSpec,
  trailingWindow,
  type DatasetDef,
  type FilterOperator,
  type Grain,
  type PeriodBinding,
  type ViewSpec,
} from './viewModel'
import type { Clarification, Company, PreviewResult, ResolvedArgument, SavedView, ViewsApi } from './viewsApi'
import './views.css'

type Status = { tone: 'info' | 'busy' | 'error' | 'ok'; text: string } | null

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export type ViewsStudioProps = {
  api: ViewsApi
  companies: Company[]
  initialCompanyKey?: string | null
  /** A spec handed over from Excel drill-through (explore context). */
  initialSpec?: ViewSpec | null
  previewPollMs?: number
}

export function ViewsStudio({ api, companies, initialCompanyKey, initialSpec, previewPollMs = 1500 }: ViewsStudioProps) {
  const [company, setCompany] = useState<Company | null>(() => companies.find((c) => c.company_key === initialCompanyKey || c.workbook_id === initialCompanyKey) || companies[0] || null)
  const [datasets, setDatasets] = useState<DatasetDef[]>([])
  const [spec, setSpec] = useState<ViewSpec | null>(null)
  const [binding, setBinding] = useState<PeriodBinding>({ mode: 'fg_period', trailing: 12 })
  const [lastChange, setLastChange] = useState<string | null>(null)
  const [describeText, setDescribeText] = useState('')
  const [resolved, setResolved] = useState<ResolvedArgument[]>([])
  const [clarification, setClarification] = useState<Clarification | null>(null)
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [views, setViews] = useState<SavedView[]>([])
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null)
  const [viewName, setViewName] = useState('')
  const [scope, setScope] = useState<'personal' | 'workspace'>('personal')
  const [excelMode, setExcelMode] = useState<'table' | 'formulas'>('table')
  const [filterDimension, setFilterDimension] = useState('')
  const [status, setStatus] = useState<Status>(null)
  const asOf = useMemo(() => latestClosedMonth(), [])

  const dataset = useMemo(() => datasets.find((d) => d.dataset_id === spec?.dataset_id) || null, [datasets, spec?.dataset_id])
  const fail = (error: unknown) => setStatus({ tone: 'error', text: error instanceof Error ? error.message : String(error) })

  const loadViews = useCallback(async (key: string) => setViews(await api.list(key)), [api])

  useEffect(() => {
    if (!company) return
    let live = true
    Promise.all([api.datasets(company.workbook_id), api.list(company.company_key)])
      .then(([catalog, saved]) => {
        if (!live) return
        setDatasets(catalog)
        setViews(saved)
        const start = initialSpec && catalog.find((d) => d.dataset_id === initialSpec.dataset_id)
        if (start && initialSpec) {
          setSpec({ ...initialSpec, company_key: company.company_key })
          setBinding({ mode: 'fixed' })
        } else if (catalog.length) {
          const first = emptySpec(catalog[0], company.company_key, asOf)
          setSpec(first.spec)
          setBinding(first.binding)
        }
      })
      .catch(fail)
    return () => {
      live = false
    }
  }, [api, asOf, company, initialSpec])

  /** Every manual edit goes through here so it is echoed back. */
  const edit = (next: ViewSpec, nextBinding: PeriodBinding = binding) => {
    if (spec) setLastChange(describeChange(spec, next))
    setSpec(next)
    setBinding(nextBinding)
    setPreview(null)
    setClarification(null)
  }

  const chooseDataset = (datasetId: string) => {
    const next = datasets.find((d) => d.dataset_id === datasetId)
    if (!next || !company) return
    const fresh = emptySpec(next, company.company_key, asOf)
    edit(fresh.spec, fresh.binding)
  }

  const chooseGrain = (grain: Grain) => {
    if (!spec) return
    const oldPeriod = periodDimension(dataset, spec.grain)
    const newPeriod = periodDimension(dataset, grain)
    const dimensions = spec.dimensions.filter((id) => id !== oldPeriod)
    const window = binding.mode === 'fg_period' ? trailingWindow(spec.period_window.end, binding.trailing, grain) : spec.period_window
    edit({ ...spec, grain, period_window: window, dimensions: newPeriod ? [newPeriod, ...dimensions] : dimensions })
  }

  const toggle = (key: 'dimensions' | 'measures', id: string) => {
    if (!spec) return
    const list = spec[key]
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
    const sort = spec.sort.filter((s) => key !== 'measures' || next.includes(s.field_id) || spec.dimensions.includes(s.field_id))
    edit({ ...spec, [key]: next, sort, top_n: sort.length ? spec.top_n : null })
  }

  const setFilter = (dimensionId: string, members: string[], operator: FilterOperator) => {
    if (!spec) return
    const others = spec.filters.filter((f) => f.dimension_id !== dimensionId)
    edit({ ...spec, filters: members.length ? [...others, { dimension_id: dimensionId, operator, member_ids: members }] : others })
  }

  const setPeriod = (next: PeriodBinding, start?: string, end?: string) => {
    if (!spec) return
    if (next.mode === 'fg_period') edit({ ...spec, period_window: trailingWindow(asOf, next.trailing, spec.grain) }, next)
    else edit({ ...spec, period_window: { start: start || spec.period_window.start, end: end || spec.period_window.end, periods: null } }, next)
  }

  const runDescribe = async () => {
    if (!company || !describeText.trim()) return
    setStatus({ tone: 'busy', text: 'Reading your description…' })
    try {
      const result = await api.describe(company.company_key, describeText)
      setResolved(result.resolved)
      if (result.needs_clarification || !result.spec) {
        setClarification(result.needs_clarification || null)
        setStatus({ tone: 'info', text: result.needs_clarification?.question || 'FuseGrid needs one more detail.' })
        return
      }
      setClarification(null)
      setSpec(result.spec)
      setBinding(result.period_binding)
      setLastChange('Filled from your description. Adjust anything below.')
      setPreview(null)
      setStatus({ tone: 'ok', text: result.summary })
    } catch (error) {
      fail(error)
    }
  }

  const applyClarification = (id: string) => {
    if (!spec || !clarification) return
    if (clarification.pointer === '/dataset_id') chooseDataset(id)
    else if (clarification.pointer === '/measures' && !spec.measures.includes(id)) toggle('measures', id)
    else if (clarification.pointer.includes('dimensions') && !spec.dimensions.includes(id)) toggle('dimensions', id)
    setClarification(null)
  }

  const runPreview = async () => {
    if (!company || !spec) return
    setStatus({ tone: 'busy', text: 'Materializing the preview…' })
    try {
      for (let attempt = 0; attempt < 40; attempt += 1) {
        const result = await api.preview(company.company_key, spec, binding)
        if (result.status === 'ready') {
          setPreview(result)
          setStatus({ tone: 'ok', text: `${(result.row_count ?? 0).toLocaleString('en-US')} rows.` })
          return
        }
        if (result.status === 'failed') throw new Error('The preview could not be materialized.')
        await sleep(previewPollMs)
      }
      throw new Error('The preview is still materializing. Try again in a moment.')
    } catch (error) {
      fail(error)
    }
  }

  const save = async () => {
    if (!company || !spec || !viewName.trim()) return fail(new Error('Name the view before saving.'))
    setStatus({ tone: 'busy', text: 'Saving…' })
    try {
      const saved = await api.save(company.company_key, viewName.trim(), spec, scope, binding)
      setSelectedViewId(saved.view_id)
      await loadViews(company.company_key)
      setStatus({ tone: 'ok', text: `Saved "${saved.name}" (${saved.scope === 'workspace' ? 'published to the workspace' : 'personal'}).` })
    } catch (error) {
      fail(error)
    }
  }

  const putInExcel = async () => {
    if (!company || !selectedViewId) return fail(new Error('Save the view first.'))
    try {
      const result = await api.putInExcel(company.company_key, selectedViewId, excelMode)
      if (!result.ok) throw new Error(result.error?.message || result.error?.code || 'The workbook could not be queued.')
      setStatus({ tone: 'ok', text: 'Queued for your workbook. Approve it in the FuseGrid pane in Excel.' })
    } catch (error) {
      fail(error)
    }
  }

  const refreshView = async (view: SavedView) => {
    setStatus({ tone: 'busy', text: `Refreshing ${view.name}…` })
    try {
      const receipt = await api.refresh(view.view_id)
      if (company) await loadViews(company.company_key)
      setStatus({ tone: 'ok', text: `${view.name}: refresh v${receipt.version} ${receipt.status}.` })
    } catch (error) {
      fail(error)
    }
  }

  const openView = (view: SavedView) => {
    setSelectedViewId(view.view_id)
    setViewName(view.name)
    setSpec(view.spec)
    setBinding(view.period_binding)
    setLastChange(`Opened "${view.name}".`)
    setPreview(null)
  }

  const summary = spec ? summarizeSpec(spec, binding, dataset) : ''
  const problems = spec ? specProblems(spec, dataset) : []
  const destinations = excelDestinations(preview?.row_count)
  const selectedView = views.find((v) => v.view_id === selectedViewId) || null
  const periodDim = spec ? periodDimension(dataset, spec.grain) : null
  const previewColumn = (dimensionId: string) => {
    const index = preview?.columns.findIndex((c) => c.id === dimensionId) ?? -1
    return index < 0 || !preview ? [] : [...new Set(preview.rows.map((row) => row[index]).filter(Boolean))]
  }

  return (
    <div className="fg-views">
      <section className="fg-views-panel" aria-label="Describe it">
        <label className="fg-views-label" htmlFor="fg-views-describe">Describe it</label><FgHelp id="views.describe" />
        <div className="fg-views-row">
          <select aria-label="Company" value={company?.company_key || ''} onChange={(e) => setCompany(companies.find((c) => c.company_key === e.target.value) || null)}>
            {companies.map((c) => <option key={c.company_key} value={c.company_key}>{c.display_name}</option>)}
          </select>
          <input
            id="fg-views-describe"
            value={describeText}
            placeholder="e.g. Revenue by account, last 6 months, only P&L"
            onChange={(e) => setDescribeText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void runDescribe() }}
          />
          <button type="button" className="btn primary" onClick={() => void runDescribe()}>Fill the builder</button>
        </div>
        {clarification && (
          <div className="fg-views-clarify" role="group" aria-label="Choose one">
            <span>{clarification.question}</span>
            {clarification.options.map((option) => (
              <button type="button" key={option.id} className="fg-views-chip" onClick={() => applyClarification(option.id)}>{option.label}</button>
            ))}
          </div>
        )}
        {resolved.length > 0 && (
          <ul className="fg-views-resolved" aria-label="What FuseGrid understood">
            {resolved.map((r) => <li key={`${r.pointer}:${r.resolved_id}`}>“{r.said}” → <b>{r.resolved_label}</b></li>)}
          </ul>
        )}
      </section>

      {spec && (
        <section className="fg-views-panel" aria-label="Build it">
          <div className="fg-views-row"><p className="fg-views-summary" data-testid="views-summary">{summary}</p><FgHelp id="views.builder" /></div>
          {lastChange && <p className="fg-views-muted" data-testid="views-last-change">{lastChange}</p>}
          <div className="fg-views-grid">
            <label>Dataset
              <select aria-label="Dataset" value={spec.dataset_id} onChange={(e) => chooseDataset(e.target.value)}>
                {datasets.map((d) => <option key={d.dataset_id} value={d.dataset_id}>{d.label.replace(/^Governed /, '')}</option>)}
              </select>
            </label>
            <label>Grain
              <select aria-label="Grain" value={spec.grain} onChange={(e) => chooseGrain(e.target.value as Grain)}>
                {(dataset?.supported_grains || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <fieldset>
              <legend>Period <FgHelp id="views.period" /></legend>
              <label><input type="radio" checked={binding.mode === 'fg_period'} onChange={() => setPeriod({ mode: 'fg_period', trailing: 12 })} /> Follow FG_Period, last
                <input type="number" min={1} max={240} aria-label="Trailing periods" disabled={binding.mode !== 'fg_period'} value={binding.mode === 'fg_period' ? binding.trailing : 12}
                  onChange={(e) => setPeriod({ mode: 'fg_period', trailing: Math.max(1, Math.min(240, Number(e.target.value) || 1)) })} /> periods
              </label>
              <label><input type="radio" checked={binding.mode === 'fixed'} onChange={() => setPeriod({ mode: 'fixed' })} /> Fixed
                <input type="month" aria-label="From" disabled={binding.mode !== 'fixed'} value={spec.period_window.start} onChange={(e) => setPeriod({ mode: 'fixed' }, e.target.value)} />
                <input type="month" aria-label="To" disabled={binding.mode !== 'fixed'} value={spec.period_window.end} onChange={(e) => setPeriod({ mode: 'fixed' }, undefined, e.target.value)} />
              </label>
            </fieldset>
          </div>
          <div className="fg-views-grid">
            <fieldset>
              <legend>Measures</legend>
              {dataset && activeMeasures(dataset).map((id) => (
                <label key={id}><input type="checkbox" checked={spec.measures.includes(id)} onChange={() => toggle('measures', id)} /> {fieldLabel(id)}</label>
              ))}
            </fieldset>
            <DerivedMeasures spec={spec} measures={dataset ? activeMeasures(dataset) : []} onChange={(derived_measures) => edit({ ...spec, derived_measures })} />
            <fieldset>
              <legend>Dimensions</legend>
              {dataset?.dimensions.filter((id) => id !== periodDim && !id.startsWith('period.')).map((id) => (
                <label key={id}><input type="checkbox" checked={spec.dimensions.includes(id)} onChange={() => toggle('dimensions', id)} /> {fieldLabel(id)}</label>
              ))}
            </fieldset>
            <fieldset>
              <legend>Filters</legend>
              <select aria-label="Filter dimension" value={filterDimension} onChange={(e) => setFilterDimension(e.target.value)}>
                <option value="">Add a filter…</option>
                {dataset?.dimensions.filter((id) => !id.startsWith('period.')).map((id) => <option key={id} value={id}>{fieldLabel(id)}</option>)}
              </select>
              {[...new Set([...spec.filters.map((f) => f.dimension_id), ...(filterDimension ? [filterDimension] : [])])].map((dimensionId) => {
                const current = spec.filters.find((f) => f.dimension_id === dimensionId)
                const operator = current?.operator || 'in'
                return (
                  <div key={dimensionId} className="fg-views-filter">
                    <b>{fieldLabel(dimensionId)}</b>
                    <select aria-label={`${fieldLabel(dimensionId)} filter mode`} value={operator === 'not_in' ? 'not_in' : 'in'} onChange={(e) => setFilter(dimensionId, current?.member_ids || [], e.target.value as FilterOperator)}>
                      <option value="in">only</option>
                      <option value="not_in">excluding</option>
                    </select>
                    <MemberPicker
                      dimensionId={dimensionId}
                      selected={current?.member_ids || []}
                      onChange={(members) => setFilter(dimensionId, members, operator)}
                      previewMembers={previewColumn(dimensionId)}
                      search={selectedView ? (q) => api.members(selectedView.view_id, dimensionId, q) : undefined}
                    />
                  </div>
                )
              })}
            </fieldset>
            <fieldset>
              <legend>Pivot, sort and top-N</legend>
              <label>Pivot into columns
                <select aria-label="Pivot" value={spec.pivot_dimensions[0] || ''} onChange={(e) => {
                  const pivot = e.target.value
                  const dims = [...spec.dimensions.filter((id) => id !== pivot), ...spec.pivot_dimensions.filter((id) => id !== pivot)]
                  edit({ ...spec, dimensions: dims, pivot_dimensions: pivot ? [pivot] : [] })
                }}>
                  <option value="">No pivot</option>
                  {[...spec.dimensions, ...spec.pivot_dimensions].map((id) => <option key={id} value={id}>{fieldLabel(id)}</option>)}
                </select>
              </label>
              <label>Sort by
                <select aria-label="Sort by" value={spec.sort[0]?.field_id || ''} onChange={(e) => edit({ ...spec, sort: e.target.value ? [{ field_id: e.target.value, descending: spec.sort[0]?.descending ?? true }] : [], top_n: e.target.value ? spec.top_n : null })}>
                  <option value="">Default order</option>
                  {[...spec.measures, ...spec.dimensions].map((id) => <option key={id} value={id}>{fieldLabel(id)}</option>)}
                </select>
              </label>
              <label><input type="checkbox" disabled={!spec.sort.length} checked={spec.sort[0]?.descending ?? true} onChange={(e) => edit({ ...spec, sort: spec.sort.map((s, i) => (i === 0 ? { ...s, descending: e.target.checked } : s)) })} /> Largest first</label>
              <label>Top
                <input type="number" min={1} max={10000} aria-label="Top N" disabled={!spec.sort.length} value={spec.top_n ?? ''} onChange={(e) => edit({ ...spec, top_n: e.target.value ? Math.floor(Number(e.target.value)) : null })} />
              </label>
            </fieldset>
          </div>
          <FgHelp id="views.limits" variant="text" label="Size limits" />
          {problems.length > 0 && <ul className="fg-views-problems" role="alert">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
          <div className="fg-views-row">
            <button type="button" className="btn secondary" disabled={problems.length > 0} onClick={() => void runPreview()}>Preview</button>
            <input aria-label="View name" placeholder="Name this view" value={viewName} onChange={(e) => setViewName(e.target.value)} />
            <select aria-label="Save scope" value={scope} onChange={(e) => setScope(e.target.value as 'personal' | 'workspace')}>
              <option value="personal">Personal</option>
              <option value="workspace">Publish to workspace</option>
            </select>
            <button type="button" className="btn primary" disabled={problems.length > 0} onClick={() => void save()}>Save</button>
            <FgHelp id="views.save" />
          </div>
        </section>
      )}

      {status && <p className={`fg-views-status ${status.tone}`} role="status">{status.text}</p>}

      {preview && (
        <section className="fg-views-panel" aria-label="Preview">
          <div className="fg-views-row">
            <b>{(preview.row_count ?? 0).toLocaleString('en-US')} rows</b>
            <span className="fg-views-muted">showing the first {preview.rows.length}</span>
          </div>
          <div className="fg-views-table-wrap">
            <table className="fg-views-table">
              <thead><tr>{preview.columns.map((c) => <th key={c.id}>{c.label}</th>)}</tr></thead>
              <tbody>{preview.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </section>
      )}

      <section className="fg-views-panel" aria-label="Put in Excel">
        <div className="fg-views-row">
          <label><input type="radio" name="fg-views-excel" checked={excelMode === 'table'} disabled={Boolean(preview) && !destinations.table} onChange={() => setExcelMode('table')} /> As a table (up to 1,000,000 rows)</label>
          <label><input type="radio" name="fg-views-excel" checked={excelMode === 'formulas'} onChange={() => setExcelMode('formulas')} /> As FUSEGRID formulas (any size)</label>
          <FgHelp id="views.formulas" />
          <button type="button" className="btn primary" disabled={!selectedViewId || (excelMode === 'table' && Boolean(preview) && !destinations.table)} onClick={() => void putInExcel()}>Put in Excel</button>
          <FgHelp id="views.excel" />
        </div>
        {preview && destinations.reason && <p className="fg-views-muted">{destinations.reason}</p>}
      </section>

      <section className="fg-views-panel" aria-label="Saved views">
        <h3>Saved views <FgHelp id="views.refresh" /></h3>
        {!views.length && <p className="fg-views-muted">No saved views yet.</p>}
        <ul className="fg-views-saved">
          {views.map((view) => (
            <li key={view.view_id} className={view.view_id === selectedViewId ? 'selected' : undefined}>
              <div><b>{view.name}</b> <span className="fg-views-muted">{view.scope}</span></div>
              <div className="fg-views-muted">{view.summary}</div>
              {view.latest_refresh && (
                <div className="fg-views-muted">
                  Refreshed {view.latest_refresh.refreshed_at?.slice(0, 16).replace('T', ' ')} UTC · {view.latest_refresh.row_count?.toLocaleString('en-US')} rows · source {view.latest_refresh.source_set_sha256.slice(0, 12)} · v{view.latest_refresh.version}
                </div>
              )}
              <div className="fg-views-row">
                <button type="button" className="btn secondary" onClick={() => openView(view)}>Open</button>
                <button type="button" className="btn secondary" onClick={() => void refreshView(view)}>Refresh</button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

/** Derived measures: a ratio (safe divide) or a difference of two governed measures. */
function DerivedMeasures({ spec, measures, onChange }: { spec: ViewSpec; measures: string[]; onChange: (next: ViewSpec['derived_measures']) => void }) {
  const [left, setLeft] = useState('')
  const [right, setRight] = useState('')
  const [op, setOp] = useState<'divide' | 'subtract'>('divide')
  const [label, setLabel] = useState('')
  const add = () => {
    if (!left || !right || !label.trim()) return
    const slug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'measure'
    const measure = (id: string) => ({ kind: 'measure', measure_id: id })
    const expression = op === 'divide'
      ? { kind: 'safe_divide', numerator: measure(left), denominator: measure(right) }
      : { kind: 'binary', operator: 'subtract', left: measure(left), right: measure(right) }
    onChange([...spec.derived_measures.filter((d) => d.measure_id !== `derived.${slug}`), { measure_id: `derived.${slug}`, label: label.trim(), expression, format_id: op === 'divide' ? 'percent' : null }])
    setLabel('')
  }
  return (
    <fieldset>
      <legend>Derived measures</legend>
      {spec.derived_measures.map((d) => (
        <button type="button" key={d.measure_id} className="fg-views-chip" onClick={() => onChange(spec.derived_measures.filter((x) => x.measure_id !== d.measure_id))} aria-label={`Remove ${d.label}`}>{d.label} ×</button>
      ))}
      <select aria-label="Derived left" value={left} onChange={(e) => setLeft(e.target.value)}><option value="">Measure…</option>{measures.map((id) => <option key={id} value={id}>{fieldLabel(id)}</option>)}</select>
      <select aria-label="Derived operator" value={op} onChange={(e) => setOp(e.target.value as 'divide' | 'subtract')}><option value="divide">÷ (ratio)</option><option value="subtract">− (difference)</option></select>
      <select aria-label="Derived right" value={right} onChange={(e) => setRight(e.target.value)}><option value="">Measure…</option>{measures.map((id) => <option key={id} value={id}>{fieldLabel(id)}</option>)}</select>
      <input aria-label="Derived label" placeholder="Label, e.g. Margin %" value={label} onChange={(e) => setLabel(e.target.value)} />
      <button type="button" className="btn secondary" onClick={add}>Add derived measure</button>
    </fieldset>
  )
}

export default ViewsStudio
